import { once } from 'lodash';
import React, {
    createContext,
    type ReactElement,
    type ReactNode,
    useContext,
} from 'react';

import type { DeepPartial } from '../types/DeepPartial';
import type { Membership, User } from '../types/Membership';
import { useFlow } from './FlowProvider';

type StepsMap<TSteps extends string> = Record<TSteps, () => ReactElement>;

interface FlowRouterContextType<TData, TSteps extends string> {
    onContinue: (updates?: DeepPartial<TData>) => Promise<void>;
    steps: StepsMap<TSteps>; // Steps is the full map between route key and the component to render.
    routes: Array<{ key: TSteps }>; // Routes is the dynamic array of routes that, at any point in time, make up the flow.
    onBack: () => void;
}

const createStateContext = once(<TData, TSteps extends string>() =>
    createContext(
        undefined as FlowRouterContextType<TData, TSteps> | undefined,
    ),
);

export const useFlowRouter = <
    TData,
    TSteps extends string,
>(): FlowRouterContextType<TData, TSteps> => {
    const context = useContext(createStateContext<TData, TSteps>());

    if (context == null) {
        throw new Error('useFlowRouter must be used within a FlowRouter.');
    }

    return context;
};

interface FlowRouterProps<TSteps extends string, TData> {
    steps: StepsMap<TSteps>;
    getRoutes: (
        data: DeepPartial<TData>,
        membership: Membership,
        currentUser?: User,
    ) => TSteps[];
    children: ReactNode;
}

export const FlowRouter = <TSteps extends string, TData>({
    steps,
    getRoutes,
    children,
}: FlowRouterProps<TSteps, TData>) => {
    const FlowRouterContext = createStateContext<TData, TSteps>();

    const {
        data,
        setData,
        index,
        setIndex,
        setIsSubmitting,
        currentUser,
        membership,
        onComplete,
        onExit,
        onError,
    } = useFlow<TData>();

    const getIsOnLastStep = (index: number, routes: TSteps[]) =>
        index === routes.length - 1;

    const routeSteps = getRoutes(data, membership, currentUser);
    const routes = routeSteps.map((route) => ({
        key: route,
    }));

    const onBack = () => {
        if (index > 0) {
            setIndex(index - 1);
        } else {
            onExit();
        }
    };

    const onContinue = async (updates?: DeepPartial<TData>) => {
        const isArray = Array.isArray(data) && Array.isArray(updates); // These should always be the same, but we aren't strictly checking against it.

        // Construct the new data object.
        // Objects are merged, arrays are fully replaced.
        // We fully replace arrays to handle the case where the data variable is an array and is (de-)populated on a single step.
        // If you want to merge top-level arrays, handle the logic inside the Step by merging updates with data.
        // See FinancialGoalsFlow MajorMoneyGoalsStep as an example.
        const updatedData = isArray
            ? updates
            : {
                  ...data,
                  ...updates,
              };

        // Set the updated data in the FlowProvider.
        if (updates != null) {
            setData(updatedData);
        }

        // Use the updated data to determine if we are on the last step.
        // We need to recompute routes because the desired routing may have changed based on updates provided in the step that is currently calling the onContinue method.
        const updatedRoutes = getRoutes(updatedData, membership, currentUser);
        const isOnLastStep = getIsOnLastStep(index, updatedRoutes);

        // If it's the last Step, trigger onComplete. Else, go to next screen.
        if (isOnLastStep) {
            setIsSubmitting(true);

            try {
                // We don't validate that the data being submitted satisfies TData, though it should. Instead, any data validity issues would cause the onComplete call to throw an error, which we then handle via the catch and onError call below.
                await onComplete(updatedData as TData);
            } catch (error) {
                onError(error as Error);
            } finally {
                setIsSubmitting(false);
                setIndex(0);
            }
        } else {
            setIndex(index + 1);
        }
    };

    return (
        <FlowRouterContext.Provider
            value={{
                onContinue,
                onBack,
                routes,
                steps,
            }}
        >
            {children}
        </FlowRouterContext.Provider>
    );
};
